import { currentTheme, getThemedGrayFraction } from '../../themes/themes';
import { getAxisDef } from '../helpers/axisDef';
import { getMappedColorStringDataPoint, TpColorDataMapperInfo } from '../helpers/colorMappingInfo';
import { hasSelectedItems, isSelectedItem } from '../helpers/misc';
import { TpAreaRenderContext } from './zoomPanCanvasHelpers';

export interface TpCustomAxisDefinition {
  labels: {
    value: number;
    text: string;
  }[];
}

const clipCache: any = {};

export function clipText(ctx: any, label: string, maxSize: number, fontSize: number) {
  if (!label) return label;
  const cacheToken = label + '___' + maxSize.toString() + '___' + fontSize.toString();
  if (clipCache[cacheToken]) return clipCache[cacheToken];
  let clippedLabel = label;
  let clippedLength = clippedLabel.length;
  while (ctx.measureText(clippedLabel).width > maxSize && clippedLength > 1) {
    clippedLength--;
    clippedLabel = label.substring(0, clippedLength) + '…';
  }
  clipCache[cacheToken] = clippedLabel;
  return clippedLabel;
}

const optTickDist = 80;

export function renderGrid(
  renderContext: TpAreaRenderContext,
  xDir: boolean,
  yDir: boolean,
  customXAxis?: TpCustomAxisDefinition,
) {
  const { mapx, mapy, mapinvx, mapinvy, areaXMin, areaXMax, areaYMin, areaYMax, pixelRatio, mapsize } = renderContext;
  const ctx = renderContext.canvasContext;
  const rangeXMin = mapinvx(areaXMin);
  const rangeXMax = mapinvx(areaXMax);
  const rangeYMin = mapinvy(areaYMin);
  const rangeYMax = mapinvy(areaYMax);

  ctx.lineWidth = 0.5 * pixelRatio;
  const style1 = renderContext.colorSchema.foreColor.opacity(0.1).toCanvasString();
  const style2 = renderContext.colorSchema.foreColor.opacity(0.3).toCanvasString();

  if (xDir) {
    if (!customXAxis) {
      if (!renderContext.axisDefX)
        renderContext.axisDefX = getAxisDef(rangeXMin, rangeXMax, (areaXMax - areaXMin) / pixelRatio / optTickDist);
      for (const axisPoint of renderContext.axisDefX) {
        ctx.strokeStyle = style1;
        if (axisPoint.label) ctx.strokeStyle = style2;
        const x = mapx(axisPoint.posit);
        ctx.beginPath();
        ctx.moveTo(x, areaYMin);
        ctx.lineTo(x, areaYMax);
        ctx.stroke();
      }
    } else {
      for (const label of customXAxis.labels) {
        ctx.strokeStyle = style2;
        const x = mapx(label.value);
        ctx.beginPath();
        ctx.moveTo(x, areaYMin);
        ctx.lineTo(x, areaYMax);
        ctx.stroke();
      }
    }
  }

  if (yDir) {
    if (!renderContext.axisDefY)
      renderContext.axisDefY = getAxisDef(
        rangeYMin,
        rangeYMax,
        Math.abs(areaYMax - areaYMin) / pixelRatio / optTickDist,
      );
    for (const axisPoint of renderContext.axisDefY) {
      ctx.strokeStyle = style1;
      if (axisPoint.label) ctx.strokeStyle = style2;
      const y = mapy(axisPoint.posit);
      ctx.beginPath();
      ctx.moveTo(areaXMin, y);
      ctx.lineTo(areaXMax, y);
      ctx.stroke();
    }
  }
}

export function renderXaxisNumerical(renderContext: TpAreaRenderContext) {
  const { mapx, mapy, mapinvx, mapinvy, areaXMin, areaXMax, pixelRatio, mapsize } = renderContext;
  const ctx = renderContext.canvasContext;
  const rangeMin = mapinvx(areaXMin);
  const rangeMax = mapinvx(areaXMax);

  if (!renderContext.axisDefX)
    renderContext.axisDefX = getAxisDef(rangeMin, rangeMax, (areaXMax - areaXMin) / pixelRatio / optTickDist);
  ctx.strokeStyle = getThemedGrayFraction(0.65).toCanvasString();
  ctx.fillStyle = renderContext.colorSchema.foreColor.toCanvasString();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  renderContext.setFont(9);
  for (const axisPoint of renderContext.axisDefX) {
    const x = mapx(axisPoint.posit);
    ctx.beginPath();
    ctx.moveTo(x, mapy(0.9));
    ctx.lineTo(x, mapy(0.6));
    ctx.stroke();
    if (axisPoint.label) ctx.fillText(axisPoint.label, x, mapy(0.5));
  }
  ctx.strokeStyle = getThemedGrayFraction(0.7).toCanvasString();
  ctx.lineWidth = mapsize(4);
  ctx.beginPath();
  ctx.moveTo(renderContext.areaXMin, renderContext.areaYMax + mapsize(2));
  ctx.lineTo(renderContext.areaXMax, renderContext.areaYMax + mapsize(2));
  ctx.stroke();
}

export function renderYaxisNumerical(renderContext: TpAreaRenderContext) {
  const { mapx, mapy, mapinvx, mapinvy, areaYMin, areaYMax, pixelRatio, mapsize } = renderContext;
  const ctx = renderContext.canvasContext;
  const rangeMin = mapinvy(areaYMin);
  const rangeMax = mapinvy(areaYMax);

  if (!renderContext.axisDefY)
    renderContext.axisDefY = getAxisDef(rangeMin, rangeMax, Math.abs(areaYMax - areaYMin) / pixelRatio / optTickDist);
  ctx.strokeStyle = getThemedGrayFraction(0.65).toCanvasString();
  ctx.fillStyle = renderContext.colorSchema.foreColor.toCanvasString();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'baseline';
  renderContext.setFont(9);
  for (const axisPoint of renderContext.axisDefY) {
    const y = mapy(axisPoint.posit);
    ctx.beginPath();
    ctx.moveTo(mapx(0.6), y);
    ctx.lineTo(mapx(0.9), y);
    ctx.stroke();
    if (axisPoint.label) {
      ctx.save();
      ctx.translate(mapx(0.0), y);
      ctx.rotate(-Math.PI / 2);
      ctx.fillText(axisPoint.label, 0, 0);
      ctx.restore();
    }
  }
  ctx.strokeStyle = getThemedGrayFraction(0.7).toCanvasString();
  ctx.lineWidth = mapsize(4);
  ctx.beginPath();
  ctx.moveTo(renderContext.areaXMax - mapsize(2), renderContext.areaYMin);
  ctx.lineTo(renderContext.areaXMax - mapsize(2), renderContext.areaYMax);
  ctx.stroke();
}

export function renderXAxisLabels(renderContext: TpAreaRenderContext, columnLabels: string[], colSortIndex?: number[]) {
  const { mapx, mapy, pixelConv, mapinvx, mapinvy, areaYMin, areaYMax, pixelInvConv, areaXMin, areaXMax } =
    renderContext;
  const ctx = renderContext.canvasContext;
  const fontSize = Math.min(9, 0.75 * pixelInvConv(Math.abs(mapx(1 / columnLabels.length) - mapx(0))));
  renderContext.setFont(fontSize);

  for (let colNr = 0; colNr < columnLabels.length; colNr++) {
    const sortedColNr = colSortIndex ? colSortIndex[colNr] : colNr;
    const label = columnLabels[sortedColNr];
    const xOffsetRender = mapx((colNr + 0.5) / columnLabels.length);
    if (xOffsetRender > areaXMin - 50 && xOffsetRender < areaXMax + 50) {
      ctx.save();
      ctx.textAlign = 'right';
      ctx.translate(xOffsetRender - pixelConv(fontSize / 2), renderContext.areaYMax + pixelConv(2));
      ctx.rotate(-Math.PI / 2);
      const labelClipped = clipText(ctx, label, renderContext.areaYMin - renderContext.areaYMax, fontSize);
      ctx.fillText(labelClipped, 0, 0);
      ctx.restore();
    }
  }
}

export function renderYAxisLabels(
  renderContext: TpAreaRenderContext,
  selectedItemsMap: { [itemId: string]: boolean } | null,
  rowLabels: string[],
  itemIds?: string[],
  rowColorMappingInfo?: TpColorDataMapperInfo,
  rowSortIndex?: number[],
  alignRight?: boolean,
) {
  const { mapx, mapy, pixelConv, mapinvx, mapinvy, areaYMin, areaYMax, pixelInvConv } = renderContext;
  const ctx = renderContext.canvasContext;
  const fontSize = Math.min(9, 0.75 * pixelInvConv(Math.abs(mapy(1 / rowLabels.length) - mapy(0))));
  renderContext.setFont(fontSize);

  if (rowColorMappingInfo?.hasColors) {
    for (let rowNr = 0; rowNr < rowLabels.length; rowNr++) {
      const sortedRowNr = rowSortIndex ? rowSortIndex[rowNr] : rowNr;
      if (itemIds && isSelectedItem(selectedItemsMap, itemIds[sortedRowNr])) {
        const [fillColorStr, strokeColorStr] = getMappedColorStringDataPoint(rowColorMappingInfo, sortedRowNr);
        ctx.fillStyle = fillColorStr;
        ctx.fillRect(
          renderContext.areaXMin,
          mapy(rowNr / rowLabels.length),
          renderContext.areaXMax - renderContext.areaXMin,
          mapy((rowNr + 1) / rowLabels.length) - mapy(rowNr / rowLabels.length),
        );
      }
    }
  } else {
    if (hasSelectedItems(selectedItemsMap)) {
      ctx.fillStyle = 'rgb(210,210,210)';
      for (let rowNr = 0; rowNr < rowLabels.length; rowNr++) {
        const sortedRowNr = rowSortIndex ? rowSortIndex[rowNr] : rowNr;
        if (itemIds && isSelectedItem(selectedItemsMap, itemIds[sortedRowNr]))
          ctx.fillRect(
            renderContext.areaXMin,
            mapy(rowNr / rowLabels.length),
            renderContext.areaXMax - renderContext.areaXMin,
            mapy((rowNr + 1) / rowLabels.length) - mapy(rowNr / rowLabels.length),
          );
      }
    }
  }

  ctx.fillStyle = currentTheme.colors.text.toCanvasString();
  let offset = renderContext.areaXMin + pixelConv(2);
  if (alignRight) {
    ctx.textAlign = 'right';
    offset = renderContext.areaXMax - pixelConv(2);
  }

  for (let rowNr = 0; rowNr < rowLabels.length; rowNr++) {
    const sortedRowNr = rowSortIndex ? rowSortIndex[rowNr] : rowNr;
    const label = rowLabels[sortedRowNr];
    if (fontSize > 3) {
      const labelClipped = clipText(
        ctx,
        label,
        renderContext.areaXMax - renderContext.areaXMin - pixelConv(2),
        fontSize,
      );
      ctx.fillText(labelClipped, offset, mapy((rowNr + 0.5) / rowLabels.length) - pixelConv(fontSize / 2));
    }
  }

  if (fontSize <= 3) {
    ctx.save();
    renderContext.setFont(12);
    ctx.fillStyle = 'rgba(0,0,0,0.3)';
    ctx.textAlign = 'center';
    ctx.translate(renderContext.areaXMin + pixelConv(10), (renderContext.areaYMin + renderContext.areaYMax) / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText('Zoom in to see labels', 0, 0);
    ctx.restore();
  }
}

/**
 * Render a set of alternating vertical strips in the given render context.
 * Use case: background for discrete axes with categorical data, such as a strip plot.
 * @param stripCount number of strips to render
 * @param renderContext context to render the strips in
 */
export function renderVerticalStripDividers(stripCount: number, renderContext: TpAreaRenderContext) {
  const { mapx } = renderContext;
  const ctx = renderContext.canvasContext;
  ctx.fillStyle = 'rgba(0,0,0,0.05)'; // todo: use variable color from styles

  for (let stripNr = 0; stripNr < stripCount; stripNr += 2) {
    const x1 = mapx((stripNr + 0.0) / stripCount);
    const x2 = mapx((stripNr + 1.0) / stripCount);
    ctx.fillRect(x1, renderContext.areaYMin, x2 - x1, renderContext.areaYMax - renderContext.areaYMin);
  }
}
